<template>
    <div id="openrouter-channel-detail">
        <AppBreadCrumb
            :paths="[
                { title: $t('message.integrations'), route: 'integrations' },
                { title: providerName, route: `integrations/openrouter/${provider}` },
                { title: channel ? channel.name : '' },
            ]"
            :select="true"
        />

        <div class="route-page-content breadcrumb-content" v-if="channel">
            <SidebarChild
                :items="menuItems"
                :selected-item="currentTab"
                :focusActive="true"
                @selected="(i) => (currentTab = i.key)"
            />
            <div class="page-content-wrapper">
                <div class="toolbar">
                    <div class="left">
                        <IconButton :on-click="goBack">
                            <span class="material-icons">chevron_left</span>
                        </IconButton>
                        <div class="icon-container">
                            <img :src="providerLogo" :alt="providerName" />
                        </div>
                        <div class="name">{{ channel.name }}</div>
                    </div>
                    <div class="right">
                        <a-button type="danger" @click="deleteModal = true">
                            {{ $t("message.deleteChannel") }}
                        </a-button>
                    </div>
                </div>

                <div v-if="currentTab === 'detail'">
                    <UpdateDetail v-model="channel" @saved="refreshChannel" />
                </div>
                <div v-if="currentTab === 'visibility'">
                    <Visibility v-model="channel" @saved="refreshChannel" />
                </div>
            </div>
        </div>
        <div class="loading" v-else>
            <a-spin />
        </div>

        <a-modal
            v-model="deleteModal"
            icon="exclamation-circle"
            :title="$t('message.deleteConfirm') || 'Kanalı Sil'"
            @cancel="deleteModal = false"
        >
            <p>{{ $t("message.deleteChannelConfirm") || "Bu kanalı silmek istediğinize emin misiniz?" }}</p>
            <template #footer>
                <a-button key="back" @click="deleteModal = false">
                    {{ $t("message.cancel") }}
                </a-button>
                <a-button key="submit" type="danger" @click="handleDeleteChannel">
                    {{ $t("chatbotv2.delete") }}
                </a-button>
            </template>
        </a-modal>
    </div>
</template>

<script>
import Vue from "vue";
import { mapActions, mapState } from "vuex";
import _ from "lodash";
import AppBreadCrumb from "../../../../../components/common/AppBreadCrumb.vue";
import SidebarChild from "../../../../../components/Sidebar/SidebarChild.vue";
import IconButton from "../../../Settings/components/IconButton.vue";
import OpenRouterMenuTypes from "./OpenRouterMenuTypes";
import UpdateDetail from "./UpdateDetail.vue";
import Visibility from "../../messenger/detail/Visibility.vue";

const PROVIDER_INFO = {
    openrouter: { name: "OpenRouter", logo: "https://static.supsis.live/common/ai/openrouter.svg" },
    openai: { name: "OpenAI", logo: "https://static.supsis.live/common/ai/openai.svg" },
    google: { name: "Google AI Studio", logo: "https://static.supsis.live/common/ai/google.svg" },
    anthropic: { name: "Anthropic", logo: "https://static.supsis.live/common/ai/anthropic.svg" },
    mistral: { name: "Mistral", logo: "https://static.supsis.live/common/ai/mistral.webp" },
    azure: { name: "Microsoft Azure", logo: "https://static.supsis.live/common/ai/microsoft.svg" },
    bedrock: { name: "Amazon Bedrock", logo: "https://static.supsis.live/common/ai/aws.svg" },
    cohere: { name: "Cohere", logo: "https://static.supsis.live/common/ai/cohere.svg" },
    xai: { name: "xAI", logo: "https://static.supsis.live/common/ai/xai.svg" },
    deepinfra: { name: "DeepInfra", logo: "https://static.supsis.live/common/ai/deepinfra.webp" },
    together: { name: "Together", logo: "https://static.supsis.live/common/ai/togetherai.svg" },
    fireworks: { name: "Fireworks", logo: "https://static.supsis.live/common/ai/fireworks.svg" },
    siliconflow: { name: "SiliconFlow", logo: "https://static.supsis.live/common/ai/siliconflow.png" },
    parasail: { name: "Parasail", logo: "https://static.supsis.live/common/ai/parasail.svg" },
    nebius: { name: "Nebius", logo: "https://static.supsis.live/common/ai/nebius.svg" },
    alibaba: { name: "Alibaba Cloud", logo: "https://static.supsis.live/common/ai/alibaba.svg" },
    xiaomi: { name: "Xiaomi", logo: "https://static.supsis.live/common/ai/xiaomi.svg" },
    sambanova: { name: "SambaNova", logo: "https://static.supsis.live/common/ai/sambanova.webp" },
    moonshot: { name: "Moonshot AI", logo: "https://static.supsis.live/common/ai/moonshot.svg" },
    minimax: { name: "Minimax", logo: "https://static.supsis.live/common/ai/minimax.svg" },
    nvidia: { name: "NVIDIA", logo: "https://static.supsis.live/common/ai/nvidia.svg" },
};

export default Vue.extend({
    name: "OpenRouterChannelDetail",
    components: {
        AppBreadCrumb,
        SidebarChild,
        IconButton,
        UpdateDetail,
        Visibility,
    },
    data() {
        const path = (_.last(this.$route.path.split("/")) || "detail").toLowerCase().trim();
        const selected = Object.values(OpenRouterMenuTypes.Items(this)).find((x) => x.key === path);
        return {
            currentTab: selected ? selected.key : "detail",
            selectedChannelId: this.$route.params.id,
            provider: this.$route.params.provider || "openrouter",
            deleteModal: false,
            channel: null,
            menuItems: OpenRouterMenuTypes.Items(this),
        };
    },
    computed: {
        ...mapState({
            channels: (state) => state.channel.channels.filter((x) => x.type === "openrouter"),
        }),
        providerName() {
            return _.get(PROVIDER_INFO, [this.provider, "name"], "OpenRouter");
        },
        providerLogo() {
            return _.get(PROVIDER_INFO, [this.provider, "logo"], PROVIDER_INFO.openrouter.logo);
        },
    },
    methods: {
        ...mapActions({
            listChannels: "channel/listChannels",
            deleteChannel: "channel/deleteChannel",
        }),
        init() {
            this.channel = this.channels.find((x) => x._id === this.selectedChannelId);
            if (this.channel) {
                this.provider = _.get(this.channel, "payload.provider", "openrouter");
                this.channel = _.cloneDeep(this.channel);
            }
        },
        goBack() {
            this.$router.push(`/integrations/openrouter/${this.provider}`);
        },
        handleDeleteChannel() {
            this.deleteChannel(this.channel._id).then(() => {
                this.$message.success(this.$t("message.channelDeletedSuccessfully") || "Kanal başarıyla silindi");
                this.goBack();
            }).catch(() => {
                this.$message.error(this.$t("message.failedToDeleteChannel") || "Kanal silinemedi");
            });
            this.deleteModal = false;
        },
        refreshChannel() {
            this.listChannels().then(() => {
                this.init();
            });
        },
    },
    mounted() {
        this.listChannels().then(() => {
            this.init();
        });
    },
    watch: {
        channels: {
            handler() {
                this.init();
            },
            deep: true,
        },
        currentTab(value) {
            const selected = Object.values(this.menuItems).find((x) => x.key === value);
            if (selected && this.channel) {
                this.$router.push(`/integrations/openrouter/${this.provider}/${this.channel._id}/detail/${selected.key}`);
            }
        },
    },
});
</script>

<style scoped lang="scss">
#openrouter-channel-detail {
    display: flex;
    flex-direction: column;
    background: var(--color-bg-default);
    position: relative;
    width: 100%;
    height: 100%;

    .toolbar {
        padding: 10px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        height: 50px;
        border-bottom: 1px solid var(--color-border-default);

        .left {
            display: flex;
            align-items: center;
            gap: 5px;

            .name {
                font-size: 18px;
                font-weight: 700;
            }

            .icon-container {
                width: 30px;
                height: 30px;
                display: flex;
                margin: 0 auto;
                align-items: center;
                justify-content: center;
                border-radius: 6px;
                overflow: hidden;
                position: relative;
                background: #fff;
                padding: 5px;

                img {
                    width: 100%;
                    height: 100%;
                    object-fit: contain;
                }
            }
        }

        .right {
            display: flex;
            justify-content: flex-end;
            gap: 5px;
        }
    }

    .loading {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 300px;
    }
}
</style>
